// Lo que dicen las tarjetas del asistente sobre este módulo, en los cuatro idiomas.
//
// Un contrato con `backend/assistant.js`, como la analítica lo es con
// `backend/analytics.js`: cada frase que aquel fichero arma con `phrase('x.y', …)`
// se busca aquí como `invoicing.assistantTools.x.y`. Una que falte no rompe nada
// —la tarjeta enseña la frase en inglés que trae el backend—, pero sale en inglés
// en medio de una pantalla en castellano. `tests/unit/assistantTools.test.js` lo
// comprueba.

const assistantTools = {
  es: {
    where: { invoice: 'Factura', workOrder: 'Orden de trabajo' },
    field: {
      amount: 'Importe', outstanding: 'Pendiente ahora', method: 'Forma de pago', reference: 'Su referencia',
      date: 'Día que llegó', customer: 'Cliente', hours: 'Horas', hourlyRate: 'La hora a', materials: 'Materiales',
      total: 'Total', to: 'Para', subject: 'Asunto', message: 'Mensaje', attached: 'Adjunto',
    },
    value: { today: 'Hoy', none: 'Ninguno', attachPdf: 'El PDF de la factura' },
    note: {
      overpaid: 'Es más de lo pendiente: se rechazará.',
      paymentDraft: 'Un borrador no admite cobros: antes hay que emitirlo.',
      invoiceNotFound: 'Esa factura no está en el archivo.',
      draftOnly: 'Es solo un borrador: no se emite, no se numera y no se manda nada a Hacienda.',
      orderUnknown: 'Facturación no supo que esa orden se terminara.',
      alreadyInvoiced: 'Esa orden ya tiene factura: se abrirá esa, no se repetirá.',
      noRate: 'No hay precio de la hora: la línea de horas llega a 0 para que la pongas tú.',
      leaves: 'Esto sale de la empresa: el cliente recibe la factura.',
      ownAccount: 'Sale desde tu propia cuenta de correo, como «Enviar por email». Si no tienes, añádela antes en Correo.',
      notIssued: 'Es un borrador: hay que emitirla en la pantalla antes de poder enviarla.',
      cancelled: 'Esta factura está anulada.',
      noAddress: 'No hay dirección: escribe una antes de enviarla.',
    },
    payment: {
      action: 'Registrar un cobro',
      summary: {
        plain: 'Registrar {amount} en la factura {invoice}',
        settles: 'Registrar {amount} en la factura {invoice}: queda saldada',
        leaves: 'Registrar {amount} en la factura {invoice}: quedan {left} pendientes',
      },
      afterSettled: 'La factura quedará saldada.',
      afterLeft: 'Quedarán {left} pendientes.',
      outcome: 'Cobro registrado en {number}: quedan {left} pendientes',
      outcomeSettled: 'Cobro registrado: {number} está saldada',
    },
    fromOrder: {
      action: 'Preparar la factura de una orden de trabajo',
      summary: 'Abrir un borrador de factura para {code}',
      after: 'Un borrador sin número: lo revisas y lo emites en la pantalla.',
      outcome: 'Borrador de factura abierto para {code}: {total}',
      outcomeExisting: '{code} ya tenía factura ({number})',
    },
    send: {
      action: 'Enviar la factura al cliente',
      summary: 'Enviar la factura {number} ({total}) a {to}: sale de la empresa',
      outcome: 'Factura {number} enviada a {to}',
    },
  },

  ca: {
    where: { invoice: 'Factura', workOrder: 'Ordre de treball' },
    field: {
      amount: 'Import', outstanding: 'Pendent ara', method: 'Forma de pagament', reference: 'La seva referència',
      date: 'Dia que va arribar', customer: 'Client', hours: 'Hores', hourlyRate: 'L\'hora a', materials: 'Materials',
      total: 'Total', to: 'Per a', subject: 'Assumpte', message: 'Missatge', attached: 'Adjunt',
    },
    value: { today: 'Avui', none: 'Cap', attachPdf: 'El PDF de la factura' },
    note: {
      overpaid: 'És més del que hi ha pendent: es rebutjarà.',
      paymentDraft: 'Un esborrany no admet cobraments: abans cal emetre\'l.',
      invoiceNotFound: 'Aquesta factura no és a l\'arxiu.',
      draftOnly: 'És només un esborrany: no s\'emet, no es numera i no s\'envia res a Hisenda.',
      orderUnknown: 'Facturació no va saber que aquesta ordre s\'acabés.',
      alreadyInvoiced: 'Aquesta ordre ja té factura: s\'obrirà aquesta, no es repetirà.',
      noRate: 'No hi ha preu de l\'hora: la línia d\'hores arriba a 0 perquè la posis tu.',
      leaves: 'Això surt de l\'empresa: el client rep la factura.',
      ownAccount: 'Surt des del teu propi compte de correu, com «Enviar per correu». Si no en tens, afegeix-lo abans a Correu.',
      notIssued: 'És un esborrany: cal emetre-la a la pantalla abans de poder-la enviar.',
      cancelled: 'Aquesta factura està anul·lada.',
      noAddress: 'No hi ha adreça: escriu-ne una abans d\'enviar-la.',
    },
    payment: {
      action: 'Registrar un cobrament',
      summary: {
        plain: 'Registrar {amount} a la factura {invoice}',
        settles: 'Registrar {amount} a la factura {invoice}: queda saldada',
        leaves: 'Registrar {amount} a la factura {invoice}: en queden {left} pendents',
      },
      afterSettled: 'La factura quedarà saldada.',
      afterLeft: 'En quedaran {left} pendents.',
      outcome: 'Cobrament registrat a {number}: en queden {left} pendents',
      outcomeSettled: 'Cobrament registrat: {number} està saldada',
    },
    fromOrder: {
      action: 'Preparar la factura d\'una ordre de treball',
      summary: 'Obrir un esborrany de factura per a {code}',
      after: 'Un esborrany sense número: el revises i l\'emets a la pantalla.',
      outcome: 'Esborrany de factura obert per a {code}: {total}',
      outcomeExisting: '{code} ja tenia factura ({number})',
    },
    send: {
      action: 'Enviar la factura al client',
      summary: 'Enviar la factura {number} ({total}) a {to}: surt de l\'empresa',
      outcome: 'Factura {number} enviada a {to}',
    },
  },

  en: {
    where: { invoice: 'Invoice', workOrder: 'Work order' },
    field: {
      amount: 'Amount', outstanding: 'Outstanding now', method: 'Method', reference: 'Their reference',
      date: 'Day it arrived', customer: 'Customer', hours: 'Hours', hourlyRate: 'Hour at', materials: 'Materials',
      total: 'Total', to: 'To', subject: 'Subject', message: 'Message', attached: 'Attached',
    },
    value: { today: 'Today', none: 'None', attachPdf: 'The invoice PDF' },
    note: {
      overpaid: 'That is more than is outstanding: it will be refused.',
      paymentDraft: 'A draft cannot take payments: it has to be issued first.',
      invoiceNotFound: 'That invoice is not on file.',
      draftOnly: 'It is only a draft: nothing is issued, numbered or sent to the tax office.',
      orderUnknown: 'Invoicing did not hear of that order being completed.',
      alreadyInvoiced: 'That order already has an invoice: it will be opened, not repeated.',
      noRate: 'There is no hourly rate set: the hours line comes in at 0 for you to price.',
      leaves: 'This leaves the company: the customer receives the invoice.',
      ownAccount: 'It goes from your own mail account, like «Send by email». Without one, add it in Mail first.',
      notIssued: 'It is a draft: it has to be issued on the screen before it can be sent.',
      cancelled: 'This invoice was cancelled.',
      noAddress: 'There is no address: write one before sending.',
    },
    payment: {
      action: 'Register a payment',
      summary: {
        plain: 'Register {amount} on invoice {invoice}',
        settles: 'Register {amount} on invoice {invoice}: settles it',
        leaves: 'Register {amount} on invoice {invoice}: {left} left outstanding',
      },
      afterSettled: 'The invoice will be settled.',
      afterLeft: '{left} will be left outstanding.',
      outcome: 'Payment registered on {number}: {left} outstanding',
      outcomeSettled: 'Payment registered: {number} is settled',
    },
    fromOrder: {
      action: 'Draft the invoice for a work order',
      summary: 'Open a draft invoice for {code}',
      after: 'A draft with no number: you check it and issue it on the screen.',
      outcome: 'Draft invoice opened for {code}: {total}',
      outcomeExisting: '{code} already had an invoice ({number})',
    },
    send: {
      action: 'Send the invoice to the customer',
      summary: 'Email invoice {number} ({total}) to {to}: it leaves the company',
      outcome: 'Invoice {number} sent to {to}',
    },
  },

  fr: {
    where: { invoice: 'Facture', workOrder: 'Ordre de travail' },
    field: {
      amount: 'Montant', outstanding: 'Restant dû', method: 'Mode de paiement', reference: 'Leur référence',
      date: 'Jour de réception', customer: 'Client', hours: 'Heures', hourlyRate: 'L’heure à', materials: 'Matériaux',
      total: 'Total', to: 'À', subject: 'Objet', message: 'Message', attached: 'Pièce jointe',
    },
    value: { today: 'Aujourd’hui', none: 'Aucun', attachPdf: 'Le PDF de la facture' },
    note: {
      overpaid: 'C’est plus que le restant dû : ce sera refusé.',
      paymentDraft: 'Un brouillon ne peut pas recevoir de paiement : il faut d’abord l’émettre.',
      invoiceNotFound: 'Cette facture n’existe pas.',
      draftOnly: 'Ce n’est qu’un brouillon : rien n’est émis, numéroté ni envoyé à l’administration fiscale.',
      orderUnknown: 'La facturation n’a pas été informée de la fin de cet ordre.',
      alreadyInvoiced: 'Cet ordre a déjà une facture : elle sera ouverte, pas répétée.',
      noRate: 'Aucun taux horaire n’est défini : la ligne d’heures arrive à 0 pour que vous la chiffriez.',
      leaves: 'Cela sort de l’entreprise : le client reçoit la facture.',
      ownAccount: 'Elle part de votre propre compte de messagerie, comme « Envoyer par e-mail ». Sans compte, ajoutez-le d’abord dans Messagerie.',
      notIssued: 'C’est un brouillon : il faut l’émettre à l’écran avant de pouvoir l’envoyer.',
      cancelled: 'Cette facture a été annulée.',
      noAddress: 'Il n’y a pas d’adresse : saisissez-en une avant l’envoi.',
    },
    payment: {
      action: 'Enregistrer un paiement',
      summary: {
        plain: 'Enregistrer {amount} sur la facture {invoice}',
        settles: 'Enregistrer {amount} sur la facture {invoice} : elle est soldée',
        leaves: 'Enregistrer {amount} sur la facture {invoice} : reste {left} dû',
      },
      afterSettled: 'La facture sera soldée.',
      afterLeft: 'Il restera {left} dû.',
      outcome: 'Paiement enregistré sur {number} : reste {left} dû',
      outcomeSettled: 'Paiement enregistré : {number} est soldée',
    },
    fromOrder: {
      action: 'Préparer la facture d’un ordre de travail',
      summary: 'Ouvrir un brouillon de facture pour {code}',
      after: 'Un brouillon sans numéro : vous le vérifiez et l’émettez à l’écran.',
      outcome: 'Brouillon de facture ouvert pour {code} : {total}',
      outcomeExisting: '{code} avait déjà une facture ({number})',
    },
    send: {
      action: 'Envoyer la facture au client',
      summary: 'Envoyer la facture {number} ({total}) à {to} : cela sort de l’entreprise',
      outcome: 'Facture {number} envoyée à {to}',
    },
  },
}

/** Los textos del módulo, con los de las tarjetas del asistente colgados de `<namespace>.assistantTools`. */
export function withAssistantTools(base, namespace) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    merged[locale] = {
      ...catalogue,
      [namespace]: { ...catalogue[namespace], assistantTools: assistantTools[locale] },
    }
  }
  return merged
}

export default assistantTools
